/**
 * InlineCollectionBand — column-bound horizontal product strip injected
 * mid-article so the user sees a relevant Fridge.com collection without
 * losing context on the surrounding article body.
 *
 * Visual treatment (Phase C revised 2026-05-05 v2):
 *  - **Stays within the article column** (no full-bleed). The TOC sidebar
 *    and editor's-pick rail remain visible alongside the band.
 *  - Soft grey card background with hairline top/bottom rules — section
 *    break without dominating the page.
 *  - Compact 2×2 product grid on desktop, single column on mobile. Cards
 *    are smaller than the bottom-of-page carousels (no rating row, tight
 *    image, brand+title only) to keep vertical footprint short.
 *  - "Browse all in [Collection] →" CTA in the header.
 *
 * The article-extractor injects two `<!--CB:N-->` markers per article (see
 * `lib/article-extraction.ts`); the blog post page-client splits on those
 * markers and renders this component in-place.
 */

import * as React from "react"
import Link from "next/link"

export interface InlineCollectionBandProduct {
  id: string | number
  brand: string
  title: string
  price?: number
  originalPrice?: number
  rating?: number
  reviews?: number
  images?: string[]
  canonical_url?: string | null
}

export interface InlineCollectionBandData {
  /** Collection slug used to build the "Browse all" link (`/collections/<slug>`) */
  slug: string
  /** Human-readable collection name shown in the band header */
  displayName: string
  /** Up to 4 products — the band shows max 4 to keep row height tight */
  products: InlineCollectionBandProduct[]
}

interface InlineCollectionBandProps {
  collection: InlineCollectionBandData
  /** Shown above the collection name. Default "Shop this collection". */
  eyebrow?: string
}

const formatUSD = (value?: number) =>
  typeof value === "number" && value > 0
    ? `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
    : ""

/**
 * Compact product cell — image (square), brand kicker, title (2-line clamp),
 * price. Smaller than ProductCarouselCard / ProductGridCard, no rating row.
 * Tap target = whole cell.
 */
function CompactCell({
  product,
}: {
  product: InlineCollectionBandProduct
}) {
  const href = product.canonical_url || `/products/${product.id}`
  const img = product.images?.[0] || "/placeholder.svg"
  const price = formatUSD(product.price)
  const hasDiscount =
    typeof product.originalPrice === "number" &&
    typeof product.price === "number" &&
    product.originalPrice > product.price

  return (
    <Link
      href={href}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 10,
        borderRadius: 8,
        background: "#fff",
        border: "1px solid var(--gray-200, #e5e7eb)",
        textDecoration: "none",
        color: "inherit",
        transition: "border-color 150ms linear, transform 150ms linear",
      }}
      className="ed-inline-collection-cell-link"
    >
      <div
        style={{
          aspectRatio: "1 / 1",
          background: "var(--gray-50, #f9fafb)",
          borderRadius: 6,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={img}
          alt={product.title ? `${product.title} | Fridge.com` : ''}
          // Absolutely positioned, NOT width/height 100% in the flow
          // (FRIDGE-489): percentage heights (100% and max-height alike) do
          // not resolve against this aspect-ratio parent, so a tall product
          // photo rendered at natural aspect — 312x386 in a 312x312 box,
          // measured live on production — and overflow:hidden clipped its
          // bottom off in every "Shop this collection" strip. inset:0 sizes
          // the img to the box unconditionally; object-fit does the letterbox.
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            padding: 8,
            boxSizing: "border-box",
          }}
        />
      </div>
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#0469f0",
          marginTop: 2,
        }}
      >
        {product.brand}
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--gray-900, #111827)",
          lineHeight: 1.3,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {product.title}
      </span>
      {price && (
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: hasDiscount ? "#dc2626" : "var(--gray-900, #111827)",
            fontVariantNumeric: "tabular-nums",
            marginTop: "auto",
          }}
        >
          {price}
        </span>
      )}
    </Link>
  )
}

export function InlineCollectionBand({
  collection,
  eyebrow = "Shop this collection",
}: InlineCollectionBandProps) {
  if (!collection?.products?.length) return null
  const products = collection.products.slice(0, 4)
  const browseHref = `/collections/${collection.slug}`

  return (
    /* Stays within the article column — no negative margins. Soft grey card
       with hairline borders to read as a section break without dominating
       the page. */
    <section
      className="ed-inline-collection-band"
      aria-label={`Shop ${collection.displayName}`}
      style={{
        margin: "32px 0",
        background: "var(--gray-50, #f9fafb)",
        border: "1px solid var(--gray-200, #e5e7eb)",
        borderRadius: 12,
        padding: "20px 22px",
      }}
    >
      {/* Header row: eyebrow + collection name on left, "Browse all" link on right */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: "1px solid var(--gray-200, #e5e7eb)",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--fridge-blue, #0469f0)",
              marginBottom: 4,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "var(--fridge-blue, #0469f0)",
              }}
            />
            {eyebrow}
          </div>
          <h3
            style={{
              fontSize: 17,
              fontWeight: 800,
              letterSpacing: "-0.015em",
              margin: 0,
              color: "var(--gray-900, #111827)",
              lineHeight: 1.25,
            }}
          >
            {collection.displayName}
          </h3>
        </div>
        <Link
          href={browseHref}
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "var(--fridge-orange, #ff9951)",
            textDecoration: "none",
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          Browse all
          <span aria-hidden style={{ fontSize: 13 }}>→</span>
        </Link>
      </div>

      {/* 2x2 grid on desktop, 1-col on mobile. Smaller cards keep the band
          short so the article reads as a continuous flow underneath. */}
      <div className="ed-inline-collection-strip">
        {products.map((p) => (
          <CompactCell key={p.id} product={p} />
        ))}
      </div>
    </section>
  )
}
